// One-click CSV export of holdings positions. Called from the Rollup + per-account
// views on the Holdings page. Builds an RFC 4180-quoted CSV in-memory and
// triggers a browser download — no server round-trip needed.

const HEADERS = [
  "Ticker",
  "Name",
  "Shares",
  "Avg cost / share",
  "Cost basis",
  "Current price",
  "Current value",
  "Gain $",
  "Gain %",
  "Forward annual income",
  "Yield %",
  "Yield on cost %",
  "% of portfolio",
  "Accounts",
];

const q = (v) => {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const round = (v, d = 2) => (v == null || isNaN(Number(v)) ? "" : Number(v).toFixed(d));

/**
 * @param {Array} positions  Rows from /holdings/summary or /holdings/accounts/:id/live.
 * @param {Object} totals    Summary totals block from same response.
 * @param {string} scopeLabel Human-readable label written into the header row & filename.
 */
export function exportHoldingsCsv({ positions, totals, scopeLabel = "All accounts" }) {
  const now = new Date();
  const stamp = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);

  const lines = [];
  lines.push(`# Dividend Portfolio Planner — Holdings export`);
  lines.push(`# Scope: ${scopeLabel}`);
  lines.push(`# Generated: ${now.toISOString()}`);
  lines.push(`# Positions: ${positions.length}`);
  lines.push(HEADERS.map(q).join(","));

  for (const p of positions) {
    const accounts = Array.isArray(p.by_account)
      ? p.by_account.map((b) => `${b.account_name} (${round(b.shares, 4)} sh)`).join(" | ")
      : "";
    lines.push([
      p.ticker,
      p.name || "",
      round(p.shares, 4),
      round(p.cost_basis_per_share, 4),
      round(p.cost_basis, 2),
      round(p.current_price, 4),
      round(p.current_value, 2),
      round(p.gain_abs, 2),
      round(p.gain_pct, 2),
      round(p.forward_annual_income, 2),
      round(p.yield_pct, 2),
      round(p.yoc_pct, 2),
      round(p.weight_pct, 2),
      accounts,
    ].map(q).join(","));
  }

  // Totals footer.
  if (totals) {
    lines.push("");
    lines.push(["TOTAL", "", "", "", round(totals.total_cost, 2), "", round(totals.total_value, 2),
                round(totals.total_gain_abs, 2), round(totals.total_gain_pct, 2),
                round(totals.forward_annual_income, 2),
                round(totals.current_yield_pct, 2), round(totals.yield_on_cost_pct, 2),
                "", ""].map(q).join(","));
  }

  const csv = lines.join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dpp-holdings-${scopeLabel.toLowerCase().replace(/\s+/g, "-")}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
